import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';

import { takeLoginTokenFromUrl } from './utils/login-token';
import { scrubEvent, scrubStrings, scrubBreadcrumb } from './utils/sentry-scrub';

// Before Sentry.init, so no URL Sentry records ever carries the login token
takeLoginTokenFromUrl();

// The hooks below keep the login token (PROD-2197) and the Community search text (PROD-2238) out
// of everything sent to Sentry, with the one clean-up in utils/sentry-scrub. A hook never throws
// (Sentry would turn the throw into an error event and lose what it was sending) and never sends
// what it could not clean. Request bodies are not recorded (no networkDetailAllowUrls), so the
// body of the saved-search report (v1/community/searches/) never reaches a replay.
const cleanOrDrop = <T>(clean: (value: T) => T, value: T): T | null => {
  try {
    return clean(value);
  } catch {
    return null;
  }
};

Sentry.init({
  dsn: 'https://9446c9f4b78de24c5edcabe71b4d50aa@o1181643.ingest.us.sentry.io/4510680805146624',
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      beforeAddRecordingEvent: (event) => cleanOrDrop(scrubStrings, event),
    }),
  ],
  beforeBreadcrumb: (breadcrumb) => cleanOrDrop(scrubBreadcrumb, breadcrumb),
  beforeSendTransaction: (event) => cleanOrDrop(scrubEvent, event),
  // Sentry keeps the original span when this hook returns null, so a span that can't be cleaned
  // goes out stripped of its two address fields instead.
  beforeSendSpan: (span) => cleanOrDrop(scrubStrings, span) ?? { ...span, description: '[Filtered]', data: {} },
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost'],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  beforeSend(event, hint) {
    const error = hint?.originalException as AxiosError | undefined;

    if (error?.isAxiosError) {
      const status = error.response?.status;

      // Ignore 401 and 404
      if (status === 401 || status === 404) {
        return null; // prevents sending to Sentry
      }
    }

    return cleanOrDrop(scrubEvent, event);
  },
});

// ---- AUTH_DEBUG helpers --------------------------------------------------
// Token fingerprint — short prefix + length so tokens are correlatable but never logged raw.
export const fingerprint = (t: unknown) => (t ? `${String(t).slice(0, 6)}…(${String(t).length})` : 'null');

// Last known user id, kept in module scope so every trail/log can carry it even from call
// sites (use-axios, post-to-app, AuthProvider) that don't have the React user object.
let currentUid: string | number | null = null;

// Breadcrumb: cheap context, sends nothing on its own — it rides along with the next
// captured event/message. Use for the step-by-step auth trail.
export const authTrail = (message: string, data?: Record<string, unknown>) =>
  Sentry.addBreadcrumb({ category: 'auth', level: 'info', message, data: { uid: currentUid, ...data } });

// Standalone, searchable entry at `warning` level (not an error). Survives the 401/404
// beforeSend filter above, so use it at decision points (token clears, 401s, recovery outcome).
export const authLog = (message: string, extra?: Record<string, unknown>) =>
  Sentry.captureMessage(message, { level: 'warning', tags: { area: 'auth' }, extra: { uid: currentUid, ...extra } });

// Attribute all subsequent events/breadcrumbs to a user by id only — never email or
// other PII/PHI. Pass a falsy value to clear it on logout/auth clear.
export const setSentryUser = (user?: { id: string | number } | null) => {
  currentUid = user ? user.id : null;
  Sentry.setUser(user ? { id: user.id } : null);
};
